import React, { useEffect, useMemo, useState } from "react";
import clsx from "clsx";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader } from "../ui/card";
import {
  downloadGatewayCertBundle,
  downloadGatewayConfigBundle,
  getGatewayProvisioningPreflight,
  listGatewayProvisioningHistory,
  listGatewayProvisioningOverview,
} from "../api/gatewayProvisioning";

const toneClass = {
  success: "bg-emerald-100 text-emerald-700",
  warn: "bg-amber-100 text-amber-700",
  muted: "bg-slate-100 text-slate-600",
  danger: "bg-rose-100 text-rose-700",
  info: "bg-blue-100 text-blue-700",
};

const formatDateTime = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString();
};

function StatusPill({ label, tone }) {
  return (
    <span className={clsx("px-2 py-1 rounded-full text-xs font-medium inline-flex items-center", toneClass[tone] || toneClass.muted)}>
      {label}
    </span>
  );
}

function MetricTile({ label, value }) {
  return (
    <div className="rounded-lg border bg-white px-4 py-3">
      <div className="text-2xl font-semibold text-slate-900">{value}</div>
      <div className="text-xs uppercase tracking-wide text-slate-500">{label}</div>
    </div>
  );
}

function AlertBanner({ notice, onClose }) {
  if (!notice) return null;
  const type = notice.type || "info";
  const color = type === "error" ? "border-rose-200 bg-rose-50 text-rose-800" : "border-emerald-200 bg-emerald-50 text-emerald-800";
  return (
    <div className={clsx("rounded-md border px-4 py-3 text-sm flex items-start justify-between gap-3", color)}>
      <div>{notice.message}</div>
      <button type="button" className="font-semibold" onClick={onClose} aria-label="Close notification">
        ×
      </button>
    </div>
  );
}

export default function ConfigOverview() {
  const [overview, setOverview] = useState(null);
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [details, setDetails] = useState({});
  const [actionState, setActionState] = useState({});
  const [notice, setNotice] = useState(null);

  const loadOverview = async () => {
    setLoading(true);
    try {
      const data = await listGatewayProvisioningOverview();
      setOverview(data);
    } catch (error) {
      setNotice({
        type: "error",
        message: error?.response?.data?.detail || "Failed to load gateway provisioning overview.",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadOverview();
  }, []);

  const filteredRows = useMemo(() => {
    const rows = overview?.items || [];
    const needle = filter.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter((row) => String(row.site_name || "").toLowerCase().includes(needle));
  }, [overview?.items, filter]);

  const summary = overview?.summary || {
    total_sites: 0,
    fully_configured_sites: 0,
    total_arrays: 0,
    total_dc_capacity_mwp: 0,
    total_ac_capacity_mw: 0,
    total_signals: 0,
    total_signals_mapped: 0,
  };

  const fetchSiteDetails = async (siteId) => {
    setDetails((current) => ({
      ...current,
      [siteId]: {
        ...(current[siteId] || {}),
        loading: true,
        error: "",
      },
    }));

    try {
      const [preflight, history] = await Promise.all([
        getGatewayProvisioningPreflight(siteId),
        listGatewayProvisioningHistory(siteId, { limit: 5, offset: 0 }),
      ]);
      setDetails((current) => ({
        ...current,
        [siteId]: {
          loading: false,
          error: "",
          preflight,
          history: history?.items || [],
        },
      }));
    } catch (error) {
      setDetails((current) => ({
        ...current,
        [siteId]: {
          ...(current[siteId] || {}),
          loading: false,
          error: error?.response?.data?.detail || "Failed to load site provisioning details.",
        },
      }));
    }
  };

  const toggleExpand = async (siteId) => {
    setExpanded((current) => ({ ...current, [siteId]: !current[siteId] }));
    if (!expanded[siteId] && !details[siteId]) {
      await fetchSiteDetails(siteId);
    }
  };

  const runExportConfig = async (row) => {
    const key = `${row.site_id}:config`;
    setActionState((current) => ({ ...current, [key]: true }));
    try {
      await downloadGatewayConfigBundle(row.site_id, { allow_incomplete: false });
      setNotice({ type: "success", message: `Gateway config exported for ${row.site_name}.` });
      await loadOverview();
      if (expanded[row.site_id]) {
        await fetchSiteDetails(row.site_id);
      }
    } catch (error) {
      const detail = error?.response?.data?.detail;
      let message = "Failed to export gateway config.";
      if (typeof detail === "string") {
        message = detail;
      } else if (detail?.message) {
        message = detail.message;
      }
      setNotice({ type: "error", message });
    } finally {
      setActionState((current) => ({ ...current, [key]: false }));
    }
  };

  const runIssueCert = async (row) => {
    if (row.has_certificate) {
      const ok = window.confirm(
        `A certificate already exists for ${row.site_name}. Reissue and replace with a new bundle?`
      );
      if (!ok) return;
    }

    const key = `${row.site_id}:cert`;
    setActionState((current) => ({ ...current, [key]: true }));
    try {
      await downloadGatewayCertBundle(row.site_id, { validity_days: 365 });
      setNotice({ type: "success", message: `mTLS bundle generated for ${row.site_name}.` });
      await loadOverview();
      if (expanded[row.site_id]) {
        await fetchSiteDetails(row.site_id);
      }
    } catch (error) {
      setNotice({
        type: "error",
        message: error?.response?.data?.detail || "Failed to issue gateway certificate.",
      });
    } finally {
      setActionState((current) => ({ ...current, [key]: false }));
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Gateway Provisioning</h1>
          <p className="text-slate-600">
            Generate signed gateway configuration exports and per-site mTLS bundles.
          </p>
        </div>
        <Button onClick={loadOverview} className="w-full md:w-auto" disabled={loading}>
          {loading ? "Refreshing…" : "Refresh status"}
        </Button>
      </div>

      <AlertBanner notice={notice} onClose={() => setNotice(null)} />

      <Card>
        <CardHeader>
          <div className="flex flex-col gap-1">
            <span className="text-lg font-semibold">Portfolio snapshot</span>
            <span className="text-sm text-slate-500">Computed from current site configuration and provisioning state.</span>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <MetricTile label="Sites tracked" value={summary.total_sites} />
            <MetricTile label="Fully configured" value={summary.fully_configured_sites} />
            <MetricTile label="Arrays modeled" value={summary.total_arrays} />
            <MetricTile
              label="DC / AC (MWp / MW)"
              value={`${Number(summary.total_dc_capacity_mwp || 0).toFixed(2)} / ${Number(summary.total_ac_capacity_mw || 0).toFixed(2)}`}
            />
          </div>
          <div className="mt-4 text-sm text-slate-600">
            Signals mapped: <span className="font-medium">{summary.total_signals_mapped}</span> /{" "}
            <span className="font-medium">{summary.total_signals}</span>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div>
              <h2 className="text-lg font-semibold">Provisioning tracker</h2>
              <p className="text-sm text-slate-500">
                Expand a site row for preflight diagnostics and latest provisioning history.
              </p>
            </div>
            <input
              type="text"
              placeholder="Filter by site name..."
              className="w-full md:w-80 rounded-md border border-slate-300 px-3 py-2 text-sm"
              value={filter}
              onChange={(event) => setFilter(event.target.value)}
            />
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 text-slate-600">
                <tr>
                  <th className="text-left px-4 py-3">Site</th>
                  <th className="text-left px-4 py-3">Configuration</th>
                  <th className="text-left px-4 py-3">Signals</th>
                  <th className="text-left px-4 py-3">Certificate</th>
                  <th className="text-right px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-6 text-center text-slate-500">
                      Loading provisioning data…
                    </td>
                  </tr>
                ) : null}
                {!loading && filteredRows.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-6 text-center text-slate-500">
                      No sites match the current filter.
                    </td>
                  </tr>
                ) : null}
                {!loading
                  ? filteredRows.map((row) => {
                      const detailsEntry = details[row.site_id];
                      const isExpanded = Boolean(expanded[row.site_id]);
                      const configBusy = Boolean(actionState[`${row.site_id}:config`]);
                      const certBusy = Boolean(actionState[`${row.site_id}:cert`]);
                      return (
                        <React.Fragment key={row.site_id}>
                          <tr className="border-t hover:bg-slate-50">
                            <td className="px-4 py-3">
                              <div className="font-medium text-slate-900">{row.site_name}</div>
                              <div className="text-xs text-slate-500">
                                ID {row.site_id} • {row.city || "—"}, {row.country || "—"}
                              </div>
                              <button
                                type="button"
                                className="mt-2 text-xs font-semibold text-brand-700 hover:text-brand-900"
                                onClick={() => toggleExpand(row.site_id)}
                              >
                                {isExpanded ? "Hide details" : "Show details"}
                              </button>
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex gap-2">
                                <StatusPill label={`Step 1: ${row.step1_status}`} tone={row.step1_tone} />
                                <StatusPill label={`Step 2: ${row.step2_status}`} tone={row.step2_tone} />
                              </div>
                              <div className="mt-2 text-xs text-slate-500">
                                Arrays: {row.counts.arrays} • Inverters: {row.counts.inverters}
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              <div>
                                {row.counts.signal_mapped} / {row.counts.signal_total}
                              </div>
                              <div className="text-xs text-slate-500">
                                Hosts: {row.counts.modbus_hosts} • Maps: {row.counts.modbus_mappings}
                              </div>
                              <div className="text-xs text-slate-500 mt-1">
                                Last export: {formatDateTime(row.last_config_export_at)}
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              <StatusPill
                                label={row.has_certificate ? "Issued" : "Missing"}
                                tone={row.has_certificate ? "success" : "warn"}
                              />
                              <div className="text-xs text-slate-500 mt-2">
                                Issued: {formatDateTime(row.last_cert_issued_at)}
                              </div>
                              <div className="text-xs text-slate-500">
                                Valid until: {formatDateTime(row.cert_valid_until)}
                              </div>
                            </td>
                            <td className="px-4 py-3 text-right">
                              <div className="flex flex-col md:flex-row gap-2 justify-end">
                                <Button
                                  className="px-3 py-2 text-sm"
                                  disabled={configBusy || certBusy}
                                  onClick={() => runExportConfig(row)}
                                >
                                  {configBusy ? "Exporting…" : "Export Gateway Config"}
                                </Button>
                                <Button
                                  className="px-3 py-2 text-sm"
                                  disabled={configBusy || certBusy}
                                  onClick={() => runIssueCert(row)}
                                >
                                  {certBusy ? "Processing…" : row.has_certificate ? "Reissue Certificate" : "Issue Gateway Certificate"}
                                </Button>
                              </div>
                            </td>
                          </tr>
                          {isExpanded ? (
                            <tr className="border-t bg-slate-50">
                              <td colSpan={5} className="px-4 py-4">
                                {detailsEntry?.loading ? (
                                  <div className="text-sm text-slate-500">Loading site details…</div>
                                ) : null}
                                {detailsEntry?.error ? (
                                  <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
                                    {detailsEntry.error}
                                  </div>
                                ) : null}
                                {detailsEntry?.preflight ? (
                                  <div className="grid lg:grid-cols-2 gap-4">
                                    <div className="rounded-lg border bg-white p-3">
                                      <div className="font-semibold text-slate-800 mb-2">Preflight</div>
                                      <div className="text-sm text-slate-600 mb-2">
                                        Ready: {detailsEntry.preflight.is_ready ? "Yes" : "No"}
                                      </div>
                                      {detailsEntry.preflight.blocking_errors?.length ? (
                                        <div className="mb-2">
                                          <div className="text-xs font-semibold uppercase text-rose-700">Blocking errors</div>
                                          <ul className="mt-1 list-disc pl-5 text-sm text-rose-700">
                                            {detailsEntry.preflight.blocking_errors.map((entry, idx) => (
                                              <li key={`${row.site_id}-err-${idx}`}>{entry}</li>
                                            ))}
                                          </ul>
                                        </div>
                                      ) : null}
                                      {detailsEntry.preflight.warnings?.length ? (
                                        <div>
                                          <div className="text-xs font-semibold uppercase text-amber-700">Warnings</div>
                                          <ul className="mt-1 list-disc pl-5 text-sm text-amber-700">
                                            {detailsEntry.preflight.warnings.map((entry, idx) => (
                                              <li key={`${row.site_id}-warn-${idx}`}>{entry}</li>
                                            ))}
                                          </ul>
                                        </div>
                                      ) : null}
                                      {!detailsEntry.preflight.blocking_errors?.length && !detailsEntry.preflight.warnings?.length ? (
                                        <div className="text-sm text-slate-500">No warnings or blocking errors.</div>
                                      ) : null}
                                    </div>
                                    <div className="rounded-lg border bg-white p-3">
                                      <div className="font-semibold text-slate-800 mb-2">Latest provisioning history</div>
                                      {!detailsEntry.history?.length ? (
                                        <div className="text-sm text-slate-500">No provisioning actions recorded yet.</div>
                                      ) : (
                                        <div className="space-y-2">
                                          {detailsEntry.history.map((entry) => (
                                            <div key={entry.id} className="rounded border border-slate-200 px-3 py-2 text-sm">
                                              <div className="flex items-center justify-between gap-2">
                                                <span className="font-medium text-slate-700">{entry.action_type}</span>
                                                <StatusPill label={entry.success ? "Success" : "Failed"} tone={entry.success ? "success" : "danger"} />
                                              </div>
                                              <div className="text-xs text-slate-500 mt-1">
                                                {formatDateTime(entry.requested_at)} • {entry.requested_by_email || "unknown user"}
                                              </div>
                                              {entry.error_message ? (
                                                <div className="text-xs text-rose-700 mt-1">{entry.error_message}</div>
                                              ) : null}
                                            </div>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                ) : null}
                              </td>
                            </tr>
                          ) : null}
                        </React.Fragment>
                      );
                    })
                  : null}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
